import { el, btn } from '@/ui/dom';
import { sfxCollect, sfxUiTick } from '@/audio/sfx';
import { hapticImpact } from '@/ui/haptics';
import { crewRaceWhat, type CrewRaceInvite } from '@/social/crewLogic';
import { helmetEl } from '@/ui/social/helmetArt';
import { ridButton } from '@/ui/social/clubUi';

/**
 * CrewRaceToast.ts — the two ways a club race invite reaches a player
 * (M140, owner spec; "Racer Club" wording + the dashboard buttons since M141):
 *
 *  - On the MENUS: a pop-up card that drops in from the top: the starter's
 *    helmet, "<NAME> WANTS TO RACE!", private/open + the city (or, M142,
 *    "5-STOP TOUR" for a club tour), and a gold JOIN
 *    beside a quiet LATER. It never blocks the screen under it (no scrim)
 *    and steps aside after 45 s; the invite stays waiting in RACER CLUB (the
 *    gold button turns into JOIN CLUB RACE).
 *  - IN A RACE: a small chat-style message slides in on the right edge and
 *    leaves on its own. It has NO buttons and takes no touches
 *    (pointer-events: none), so a stray thumb mid-race can never yank the
 *    player out of the race they are in. When the race ends, the menu pop-up
 *    takes over (CrewController decides when).
 *
 * Above the menu screens (z 94), under the confirm/sheets (96) and the
 * rotate-phone overlay (200).
 */

const SIDE_MS = 6500;
const POPUP_MS = 45_000;

export interface CrewInvitePopupOptions {
  onJoin: () => void;
  onLater: () => void;
  /** The pop-up stepped aside on its own after POPUP_MS (no answer). */
  onTimeout?: () => void;
  reducedMotion: boolean;
}

export class CrewRaceToast {
  private readonly layer: HTMLDivElement;
  private popup: { el: HTMLDivElement; raceId: string; timer: number } | null = null;
  private side: { el: HTMLDivElement; timer: number } | null = null;

  constructor(parent: HTMLElement) {
    this.layer = el('div', 'crewtoast-layer', parent);
  }

  get popupRaceId(): string | null {
    return this.popup?.raceId ?? null;
  }

  showPopup(race: CrewRaceInvite, o: CrewInvitePopupOptions): void {
    this.hidePopup();
    const card = el('div', 'crewtoast-pop', this.layer);
    card.classList.toggle('reduced', o.reducedMotion);
    card.setAttribute('role', 'alertdialog');
    const tour = race.tourLen > 0;
    card.setAttribute(
      'aria-label',
      `${race.starterName || 'Your club'} started a club ${tour ? 'tour' : 'race'}`,
    );
    card.appendChild(helmetEl(race.starterHelmet, 'crew-helmet crewtoast-helmet'));
    const text = el('div', 'crewtoast-text', card);
    el('div', 'crewtoast-kicker', text).textContent =
      `${tour ? 'CLUB TOUR' : 'CLUB RACE'} · ${race.crewName.toUpperCase()}`;
    el('div', 'crewtoast-title', text).textContent =
      `${(race.starterName || 'YOUR CLUB').toUpperCase()} WANTS TO RACE!`;
    const meta = el('div', 'crewtoast-meta', text);
    el('span', `crewtoast-chip ${race.visibility}`, meta).textContent =
      race.visibility === 'public' ? (tour ? 'OPEN' : 'OPEN RACE') : 'CLUB ONLY';
    // M142: what it IS — the city, or "5-STOP TOUR".
    el('span', 'crewtoast-chip', meta).textContent = crewRaceWhat(race);
    const actions = el('div', 'crewtoast-actions', card);
    ridButton(actions, { variant: 'gold', label: 'JOIN', small: true, className: 'crewtoast-join' }, () => {
      this.hidePopup();
      o.onJoin();
    });
    btn('LATER', 'btn btn-ghost crewtoast-later', actions, () => {
      this.hidePopup();
      o.onLater();
    });
    const timer = window.setTimeout(() => {
      if (this.popup?.el !== card) return;
      this.hidePopup();
      o.onTimeout?.();
    }, POPUP_MS);
    this.popup = { el: card, raceId: race.id, timer };
    if (!o.reducedMotion) {
      sfxCollect();
      hapticImpact();
    }
  }

  hidePopup(raceId?: string): void {
    if (!this.popup || (raceId && this.popup.raceId !== raceId)) return;
    const { el: card, timer } = this.popup;
    this.popup = null;
    window.clearTimeout(timer);
    card.classList.add('out');
    window.setTimeout(() => card.remove(), 260);
  }

  /** The in-race side message. No buttons; leaves on its own. */
  showSide(race: CrewRaceInvite, reducedMotion: boolean): void {
    this.hideSide();
    const pill = el('div', 'crewtoast-side', this.layer);
    pill.classList.toggle('reduced', reducedMotion);
    pill.setAttribute('role', 'status');
    pill.appendChild(helmetEl(race.starterHelmet, 'crew-helmet crewtoast-side-helmet'));
    const t = el('div', 'crewtoast-side-text', pill);
    el('div', 'crewtoast-side-title', t).textContent =
      `${(race.starterName || 'YOUR CLUB').toUpperCase()} STARTED A CLUB ${race.tourLen > 0 ? 'TOUR' : 'RACE'}`;
    el('div', 'crewtoast-side-sub', t).textContent = 'JOIN WHEN THIS RACE ENDS';
    sfxUiTick();
    const timer = window.setTimeout(() => this.hideSide(), SIDE_MS);
    this.side = { el: pill, timer };
  }

  hideSide(): void {
    if (!this.side) return;
    const { el: pill, timer } = this.side;
    this.side = null;
    window.clearTimeout(timer);
    pill.classList.add('out');
    window.setTimeout(() => pill.remove(), 260);
  }

  hideAll(): void {
    this.hidePopup();
    this.hideSide();
  }
}
